
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { PlusCircle, Calendar, CalendarCheck, ArrowUp, ListFilter, Receipt, MoreHorizontal, Edit2, Trash2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { pt } from "date-fns/locale";
import { toast } from "sonner";

// Mock data
const pagamentosPendentes = [
  { id: '1', descricao: 'Fornecimento de Inversores', valor: 5200, dataVencimento: new Date('2025-04-18'), fornecedor: 'Solar Equipamentos Ltda', status: 'pendente' },
  { id: '2', descricao: 'Aluguel da Sede', valor: 2800, dataVencimento: new Date('2025-04-10'), fornecedor: 'Imobiliária Central', status: 'atrasado' },
  { id: '3', descricao: 'Materiais de Escritório', valor: 450, dataVencimento: new Date('2025-04-20'), fornecedor: 'Papelaria Total', status: 'pendente' },
];

const pagamentosRecentes = [
  { id: '4', descricao: 'Serviços de Contabilidade', valor: 1200, dataPagamento: new Date('2025-04-02'), fornecedor: 'Contábil Assessoria', status: 'pago' },
  { id: '5', descricao: 'Internet e Telefonia', valor: 350, dataPagamento: new Date('2025-03-30'), fornecedor: 'Telecom Brasil', status: 'pago' },
];

const PagamentosTabContent: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("pendentes");
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedPagamento, setSelectedPagamento] = useState<any>(null);

  const handleAgendarPagamento = () => {
    navigate("/financeiro/pagamentos/agendar");
  };

  const handleRegistrarPagamento = () => {
    navigate("/financeiro/pagamentos/pagar");
  };

  const handleConfirmarPagamento = (pagamento: any) => {
    setSelectedPagamento(pagamento);
    setShowConfirmDialog(true);
  };

  const handleEditarPagamento = (pagamento: any) => {
    // Em uma implementação real, você redirecionaria para a página de edição
    navigate(`/financeiro/pagamentos/editar/${pagamento.id}`);
    toast.info(`Editando pagamento: ${pagamento.descricao}`);
  };

  const handleExcluirPagamento = (pagamento: any) => {
    setSelectedPagamento(pagamento);
    setShowDeleteDialog(true);
  };

  const confirmarPagamento = () => {
    // Aqui implementaria a lógica para registrar o pagamento
    console.log("Pagamento confirmado:", selectedPagamento);
    setShowConfirmDialog(false);
    toast.success(`Pagamento de ${selectedPagamento.descricao} confirmado com sucesso!`);
    // Atualizar o estado ou fazer uma chamada de API
  };

  const confirmarExclusao = () => {
    // Aqui implementaria a lógica para excluir o pagamento
    console.log("Pagamento excluído:", selectedPagamento);
    setShowDeleteDialog(false);
    toast.success(`Pagamento de ${selectedPagamento.descricao} excluído com sucesso!`);
    // Atualizar o estado ou fazer uma chamada de API
  };

  // Renderiza o menu de ações (editar e excluir) para cada item
  const renderAcoesMenu = (pagamento: any) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
          <span className="sr-only">Abrir menu</span>
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => handleEditarPagamento(pagamento)}>
          <Edit2 className="mr-2 h-4 w-4" />
          Editar
        </DropdownMenuItem>
        <DropdownMenuItem 
          onClick={() => handleExcluirPagamento(pagamento)} 
          className="text-red-600"
        >
          <Trash2 className="mr-2 h-4 w-4" />
          Excluir
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <div className="space-y-6">
      <div className="financeiro-card p-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
          <div>
            <h2 className="text-xl font-semibold text-financeiro-text-DEFAULT">Pagamentos</h2>
            <p className="text-financeiro-text-light mt-1">Gerencie seus pagamentos a fornecedores e prestadores</p>
          </div>
          <div className="flex flex-wrap gap-2 mt-4 md:mt-0">
            <Button 
              onClick={handleAgendarPagamento}
              className="bg-financeiro-accent-DEFAULT hover:bg-financeiro-accent-dark"
            >
              <Calendar className="h-4 w-4 mr-2" /> Agendar Pagamento
            </Button>
            <Button 
              variant="outline" 
              onClick={handleRegistrarPagamento}
              className="border-financeiro-border-DEFAULT text-financeiro-text-DEFAULT"
            >
              <CalendarCheck className="h-4 w-4 mr-2" /> Registrar Pagamento
            </Button>
          </div>
        </div>

        <Tabs defaultValue="pendentes" value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="mb-6 border-b border-financeiro-border-light">
            <TabsTrigger 
              value="pendentes" 
              className={`px-4 py-2 border-b-2 transition-colors ${
                activeTab === "pendentes" 
                  ? "border-financeiro-accent-DEFAULT text-financeiro-accent-DEFAULT" 
                  : "border-transparent text-financeiro-text-light hover:text-financeiro-text-DEFAULT"
              }`}
            >
              Pendentes
            </TabsTrigger>
            <TabsTrigger 
              value="historico" 
              className={`px-4 py-2 border-b-2 transition-colors ${
                activeTab === "historico" 
                  ? "border-financeiro-accent-DEFAULT text-financeiro-accent-DEFAULT" 
                  : "border-transparent text-financeiro-text-light hover:text-financeiro-text-DEFAULT"
              }`}
            >
              Histórico
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="pendentes">
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium">Pagamentos Pendentes</h3>
                <Button variant="ghost" size="sm" className="text-financeiro-text-light">
                  <ListFilter className="h-4 w-4 mr-2" /> Filtrar
                </Button>
              </div>
              
              {pagamentosPendentes.length > 0 ? (
                <div className="grid grid-cols-1 gap-4">
                  {pagamentosPendentes.map((pagamento) => (
                    <Card key={pagamento.id} className={`border-l-4 ${pagamento.status === 'atrasado' ? 'border-l-red-500' : 'border-l-yellow-500'}`}>
                      <CardContent className="p-4">
                        <div className="flex flex-col md:flex-row justify-between">
                          <div className="space-y-1">
                            <h4 className="font-medium">{pagamento.descricao}</h4>
                            <p className="text-sm text-gray-500">Fornecedor: {pagamento.fornecedor}</p>
                            <p className="text-sm">
                              Vencimento: {format(pagamento.dataVencimento, "dd 'de' MMMM 'de' yyyy", { locale: pt })}
                              {pagamento.status === 'atrasado' && (
                                <span className="ml-2 text-xs font-medium text-red-600 bg-red-50 px-2 py-0.5 rounded-full">
                                  Atrasado
                                </span>
                              )}
                            </p>
                          </div>
                          <div className="mt-4 md:mt-0 flex flex-col md:items-end">
                            <div className="flex justify-end items-center">
                              <p className="text-lg font-semibold text-red-600 mr-2">
                                {pagamento.valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                              </p>
                              {renderAcoesMenu(pagamento)}
                            </div>
                            <Button 
                              className="mt-2 bg-gray-600 hover:bg-gray-700"
                              size="sm"
                              onClick={() => handleConfirmarPagamento(pagamento)}
                            >
                              <ArrowUp className="h-3 w-3 mr-1" /> Registrar Pagamento
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <Card className="border-dashed border-2 p-6">
                  <div className="text-center text-gray-500">
                    <CalendarCheck className="h-12 w-12 mx-auto mb-3 opacity-30" />
                    <p className="mb-2">Nenhum pagamento pendente</p>
                    <p className="text-sm mb-4">Todos os seus pagamentos foram processados</p>
                    <Button 
                      onClick={handleAgendarPagamento}
                      className="bg-financeiro-accent-DEFAULT hover:bg-financeiro-accent-dark"
                    >
                      <PlusCircle className="h-4 w-4 mr-2" /> Agendar Novo Pagamento
                    </Button>
                  </div>
                </Card>
              )}
            </div>
          </TabsContent>
          
          <TabsContent value="historico">
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium">Pagamentos Recentes</h3>
                <Button variant="ghost" size="sm" className="text-financeiro-text-light">
                  <ListFilter className="h-4 w-4 mr-2" /> Filtrar
                </Button>
              </div>
              
              {pagamentosRecentes.length > 0 ? (
                <div className="grid grid-cols-1 gap-4">
                  {pagamentosRecentes.map((pagamento) => (
                    <Card key={pagamento.id} className="border-l-4 border-l-green-500">
                      <CardContent className="p-4">
                        <div className="flex flex-col md:flex-row justify-between">
                          <div className="space-y-1">
                            <h4 className="font-medium">{pagamento.descricao}</h4>
                            <p className="text-sm text-gray-500">Fornecedor: {pagamento.fornecedor}</p>
                            <p className="text-sm">
                              Pago em: {format(pagamento.dataPagamento, "dd 'de' MMMM 'de' yyyy", { locale: pt })}
                              <span className="ml-2 text-xs font-medium text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                                Pago
                              </span>
                            </p>
                          </div>
                          <div className="mt-4 md:mt-0 flex flex-col md:items-end">
                            <div className="flex justify-end items-center">
                              <p className="text-lg font-semibold text-red-600 mr-2">
                                -{pagamento.valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                              </p>
                              {renderAcoesMenu(pagamento)}
                            </div>
                            <div className="flex space-x-2 mt-2">
                              <Button 
                                size="sm"
                                variant="outline"
                              >
                                <Receipt className="h-3 w-3 mr-1" /> Comprovante
                              </Button>
                              <Button 
                                size="sm"
                                variant="outline"
                              >
                                Ver Detalhes
                              </Button>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <Card className="border-dashed border-2 p-6">
                  <div className="text-center text-gray-500">
                    <CalendarCheck className="h-12 w-12 mx-auto mb-3 opacity-30" />
                    <p className="mb-2">Nenhum pagamento registrado</p>
                    <p className="text-sm mb-4">Registre seus pagamentos para visualizar o histórico</p>
                  </div>
                </Card>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Diálogo de confirmação de pagamento */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Confirmar Pagamento</DialogTitle>
            <DialogDescription>
              Registre o pagamento de {selectedPagamento?.descricao} no valor de {selectedPagamento?.valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-gray-500">
              Fornecedor: {selectedPagamento?.fornecedor}
            </p>
            <p className="text-sm text-gray-500">
              Data de vencimento: {selectedPagamento?.dataVencimento && format(selectedPagamento.dataVencimento, "dd/MM/yyyy")}
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirmDialog(false)}>Cancelar</Button>
            <Button onClick={confirmarPagamento} className="bg-gray-600 hover:bg-gray-700">
              <CalendarCheck className="h-4 w-4 mr-2" /> Confirmar Pagamento
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Diálogo de confirmação de exclusão */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Confirmar Exclusão</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja excluir o pagamento de {selectedPagamento?.descricao} no valor de {selectedPagamento?.valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}?
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-gray-500">
              Esta ação não pode ser desfeita.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>Cancelar</Button>
            <Button variant="destructive" onClick={confirmarExclusao}>
              <Trash2 className="h-4 w-4 mr-2" /> Confirmar Exclusão
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PagamentosTabContent;
